import Check from '../Check';
import type { CheckSubject } from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import RESERVED_WORDS, { GENERATOR_RESERVED_WORDS } from './ReservedWords';

function isLegalJavaIdentifier(name: string): boolean {
  // starts with a letter or an underscore, followed by zero or more word
  // characters (= [a-zA-Z0-9_]). In theory also a $ is allowed, but that is used
  // by the MERODE code generator as a separator, so don't allow it.
  return /^[a-zA-Z_][\w]*$/.test(name);
}

function isReservedWord(name: string): boolean {
  return RESERVED_WORDS.includes(name.toLowerCase());
}

function isGeneratorReservedWord(name: string): boolean {
  return GENERATOR_RESERVED_WORDS.includes(name.toLowerCase());
}

function hasUnderscore(name: string): boolean {
  return name.includes('_');
}

export function doCheck(
  name: string,
  check: Check<CheckSubject> | null = null,
  subject: CheckSubject | null = null
): Report {
  const report = new Report();
  // `Report.addMessage` types its optional `check`/`subject` params as
  // `| undefined`; this public API historically passes `null` (stored
  // unchanged on the Message). The casts below bridge the `null` vs
  // `undefined` typing gap and are erased at runtime, so the literal value is
  // passed through unchanged — byte-identical behaviour.
  if (!isLegalJavaIdentifier(name)) {
    report.addMessage(
      MessageType.ERROR,
      `Name '${name}' is not a legal java identifier.`,
      check as Check<CheckSubject> | undefined,
      subject as CheckSubject | undefined
    );
  }
  if (isReservedWord(name)) {
    report.addMessage(
      MessageType.ERROR,
      `Name '${name.toLowerCase()}' is a reserved word.`,
      check as Check<CheckSubject> | undefined,
      subject as CheckSubject | undefined
    );
  }
  if (isGeneratorReservedWord(name)) {
    report.addMessage(
      MessageType.ERROR,
      `Name '${name.toLowerCase()}' collides with the generated Java code.`,
      check as Check<CheckSubject> | undefined,
      subject as CheckSubject | undefined
    );
  }
  if (hasUnderscore(name)) {
    report.addMessage(
      MessageType.WARNING,
      `Name ${name} cannot contain '_'`,
      check as Check<CheckSubject> | undefined,
      subject as CheckSubject | undefined
    );
  }
  if (report.isEmpty()) {
    report.addMessage(
      MessageType.INFORMATION,
      `Name '${name}' is valid.`,
      check as Check<CheckSubject> | undefined,
      subject as CheckSubject | undefined
    );
  }
  return report;
}

/**
 * Check the name of an element.
 *
 * Checks:
 * - if it is not a reserverd word
 * - if it is a legal java identifier
 */
class NameCheck<TElement extends CheckSubject = CheckSubject> extends Check<TElement> {
  static description = 'Check element name';
  static checkInput(name: string): Report {
    return doCheck(name);
  }

  check(): Report {
    // The default NameCheck runs on elements whose `name` is a plain string
    // (object/attribute/event/method/fsm/state/data type/transition).
    // DependencyNameCheck overrides this to check the master/dependent names.
    this.report = doCheck((this.element as { name: string }).name, this, this.element);
    return this.report;
  }
}

export default NameCheck;
